import { Key, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { BsPlusLg } from 'react-icons/bs';
import { BiMinus } from 'react-icons/bi';

import { AuthContext, useAuth } from 'context/AuthContext';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import Box from 'components/ui/box';
import {
  getNumberParticipationsEvaluated,
  getPlayerInfo,
  getServerTime,
  getTasksNeedingApprovalParticipants,
  hasManagersOnMyTeam,
} from 'services/dashboard.service';
import { TaskItem } from 'components/task-item';
import Header from '../components/Header';
import { getStorageItem } from 'utils/storage';

import ImgFeedback from 'assets/img/comportamento/bg-missoes-not-found.png';

import './styles.scss';
import { ParticipationsEvaluated_Interface, User } from 'types/user';
import { UserAvatar } from 'components/user-avatar';
import { findLateralMenagers } from 'services/profile.service';
import CustomSelect from 'components/custom-select';
import { HiOutlineHandThumbDown, HiOutlineHandThumbUp } from 'react-icons/hi2';
import { MdModeStandby } from 'react-icons/md';
import { NotFound } from 'pages/NotFound';
import { GetVersion } from 'services/utils';

export default function AnalisarParticipacaoEquipe() {
  const navigate = useNavigate();
  const { user, userInfo, realCycle, cycleSelected, setCycleSelected } = useAuth();

  const [realDatetime, setRealDatetime] = useState<number>(0);
  const [internalUser, setInternalUser] = useState<User>();
  const [gestor, setIsGestor] = useState(false);

  const [stages, setStages] = useState<any>();
  const [taskCycles, setTaskCycles] = useState<any>();
  const [playerInfo, setPlayerInfo] = useState<any>();

  const [stepActive, setStepActive] = useState<string>();
  const [showMore, setShowMore] = useState(false);
  const [numberOfElements, setNumberOfElements] = useState(3);
  const [optionsCycle, setOptionsCycle] = useState<any[] | undefined>([]);
  const [activeItem, setActiveItem] = useState<any>();
  const [tasks, setTasks] = useState<any[]>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let interv: any = undefined;

    getServerTime().then(timestamp => {
      setRealDatetime(timestamp);

      interv = window.setInterval(() => {
        setRealDatetime(prevstate => prevstate + 1000);
      }, 1000);
    });

    return () => clearInterval(interv);
  }, []);

  const isVacationManager = useMemo(() => !!internalUser && !!user && internalUser._id !== user._id, [internalUser, user]);

  const canViewPage = useMemo(
    () =>
      !!userInfo?.ro.match(/(3GUE|2GUT|2GCA|2GUA)/gi) ||
      (Array.isArray(userInfo?.mad_dupla) &&
        userInfo?.mad_dupla?.some(md => typeof md.ro === 'string' && !!md.ro.match(/(3GUE|2GUT|2GCA|2GUA)/gi))) ||
      !!userInfo?.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi) ||
      (Array.isArray(userInfo?.mad_dupla) &&
        userInfo?.mad_dupla?.some(md => typeof md.ro === 'string' && !!md.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi))) ||
      userInfo?.cod_funcao === 3002 ||
      userInfo?.cod_funcao === 3003 ||
      userInfo?.cod_funcao === 4005 ||
      userInfo?.cod_funcao === 4007,
    [userInfo],
  );

  const activeStepObj = useMemo(() => stages?.find((item: any) => item._id === stepActive), [stepActive, stages]);

  const resume: {
    approveds: number;
    rejecteds: number;
    awaiting: number;
    total: number;
  } | null = useMemo(() => {
    if (!Array.isArray(tasks) || !tasks.length) return null;

    const approveds = tasks.filter(x => x.participants_approval?.status === 'approved');
    const rejecteds = tasks.filter(x => x.participants_approval?.status === 'rejected');
    const awaiting = tasks.filter(x => x.participants_approval?.status === 'pending');

    return {
      approveds: approveds.length || 0,
      rejecteds: rejecteds.length || 0,
      awaiting: awaiting.length || 0,
      total: tasks.length,
    };
  }, [tasks]);

  const clarifyingText = useMemo(() => {
    // Total de participantes: 21 à 30
    if (!!playerInfo?.ro.match(/(3GUE|2GUT|2GCA|2GUA)/gi)) {
      return (
        <>
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
            Como funciona a autorização do número de participantes em Realização Destaque?
          </p>
          <p tabIndex={0} className="text-white text-base font-thin m-0">
            O 3º nível gerencial de UE ou 2º nível de UT, UA ou CA revisam as realizações com 21 à 30 participantes. Sua
            autorização é solicitada para confirmar ou recusar a quantidade de participantes, podendo devolvê-la ao gestor.
          </p>
        </>
      );
    }
    // Total de participantes: 31 à 50
    else if (!!playerInfo?.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi)) {
      return (
        <>
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
            Como funciona a autorização do número de participantes em Realização Destaque?
          </p>
          <p tabIndex={0} className="text-white text-base font-thin m-0">
            O 2º nível gerencial de UE ou 1º nível de UT, UA ou CA revisam as realizações com 31 à 50 participantes. Sua
            autorização é solicitada para confirmar ou recusar a quantidade de participantes, podendo devolvê-la ao gestor.
          </p>
        </>
      );
    }
    // Total de participantes: 51 à 100
    else if (playerInfo?.cod_funcao === 3003 || playerInfo?.cod_funcao === 4005 || playerInfo?.cod_funcao === 4007) {
      return (
        <>
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
            Como funciona a autorização do número de participantes em Realização Destaque?
          </p>
          <p tabIndex={0} className="text-white text-base font-thin m-0">
            Diretor ou Gerente Geral UE revisam as realizações com 51 à 100 participantes. Sua autorização é solicitada para
            confirmar ou recusar a quantidade de participantes, podendo devolvê-la ao gestor.
          </p>
        </>
      );
    }
    // Total de participantes: maior que 100
    else if (playerInfo?.cod_funcao === 3002) {
      return (
        <>
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-4">
            Como funciona a autorização do número de participantes em Realização Destaque?
          </p>
          <p tabIndex={0} className="text-white text-base font-thin m-0">
            Vice-Presidentes revisam as realizações com mais de 100 participantes. Sua autorização é solicitada para confirmar ou
            recusar a quantidade de participantes, podendo devolvê-la ao gestor.
          </p>
        </>
      );
    }
    // Nenhuma das condições acima
    else {
      return <></>;
    }
  }, [playerInfo]);

  const getUserInfo = useCallback(async () => {
    try {
      const playerInfoResult = await getPlayerInfo(internalUser?._id);

      if (playerInfoResult) {
        setIsGestor(
          (playerInfoResult.cod_etapa && playerInfoResult.cod_etapa > 0 && playerInfoResult.cod_etapa <= 5) ||
            (Array.isArray(playerInfoResult?.mad_dupla) &&
              playerInfoResult.mad_dupla.some(
                (md: any) => typeof md.cod_etapa === 'number' && md.cod_etapa > 0 && md.cod_etapa <= 5,
              )),
        );
        setPlayerInfo(playerInfoResult);

        setLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [internalUser?._id]);

  const loadData = useCallback(async () => {
    if (!activeStepObj) return;

    const data = await getTasksNeedingApprovalParticipants(
      activeStepObj.date_start,
      activeStepObj.date_finish,
      playerInfo?.chave,
    );

    setTasks(data);

    setLoading(false);
  }, [activeStepObj, playerInfo?.chave]);

  useEffect(() => {
    if (!realCycle) return;

    const cycleOptions = realCycle.map((cycle: any) => {
      cycle.label = 'Ciclo ' + cycle.title;
      cycle.value = cycle._id;

      return cycle;
    });

    setOptionsCycle(cycleOptions);

    if (!activeItem) {
      setActiveItem(cycleOptions.find(x => x.value === cycleSelected?._id) ? cycleSelected?._id : cycleOptions[0].value);
      setTaskCycles(realCycle.filter((item: any) => item.year > 2022));
    }

    setStepActive(realCycle?.find((item: any) => item._id === activeItem)?.steps[0]._id);
  }, [activeItem, cycleSelected?._id, realCycle]);

  // useEffect(() => {
  //   if (!userInfo || !realCycle || !stages || !activeStepObj) return;

  //   setLoading(true);
  //   loadData();
  // }, [userInfo, realCycle, stages, activeStepObj, loadData]);

  useEffect(() => {
    if (!internalUser || !realCycle || !stages || !activeStepObj) return;

    setLoading(true);
    getUserInfo();
  }, [activeStepObj, getUserInfo, internalUser, realCycle, stages]);

  useEffect(() => {
    if (!gestor) return;

    setLoading(true);

    loadData();
  }, [gestor, loadData]);

  useEffect(() => {
    if (!Array.isArray(stages)) return;

    if (stages.length <= numberOfElements) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  }, [numberOfElements, stages]);

  useEffect(() => {
    if (!activeItem) return;

    const filtredData = taskCycles?.filter((item: any) => item._id === activeItem);

    if (!!filtredData?.length) {
      setStages(filtredData[0].steps);
    }
  }, [activeItem, taskCycles]);

  useEffect(() => {
    if (!user) return;

    async function handleUser() {
      if (!!user) {
        const storedManager = getStorageItem('manager_lateralidade');

        if (!!storedManager && storedManager.length) {
          const vacationManagers = await findLateralMenagers(undefined, storedManager);

          const vacationManager = vacationManagers?.length
            ? vacationManagers.find((x: { _id: any }) => x._id === storedManager)
            : null;

          if (!!vacationManager?._id && !!vacationManager.extra?.id) {
            setInternalUser(vacationManager);
          } else {
            setInternalUser(user);
          }
        } else {
          setInternalUser(user);
        }
      }
    }

    async function handleUserWLoading() {
      setLoading(true);
      // console.log('handleUserWLoading');
      handleUser();
    }

    handleUser();

    document.addEventListener('reload:lateralidade', handleUserWLoading, false);

    return () => {
      document.removeEventListener('reload:lateralidade', handleUserWLoading);
    };
  }, [user]);

  function getIndexByValue(array: any, value: string) {
    return array.findIndex((element: any) => element.value === value);
  }

  const getCanApproveParticipants = (task: any): boolean => {
    const totalParticipants = task?.team?.length || 0;

    if (
      (!!playerInfo?.ro.match(/(3GUE|2GUT|2GCA|2GUA|2GUE|1GUT|1GUA|1GCA)/gi) ||
        playerInfo?.cod_funcao === 3003 ||
        playerInfo?.cod_funcao === 4005 ||
        playerInfo?.cod_funcao === 4007 ||
        playerInfo?.cod_funcao === 3002) &&
      totalParticipants >= 21 &&
      totalParticipants <= 30
    ) {
      return true;
    } else if (
      (!!playerInfo?.ro.match(/(2GUE|1GUT|1GUA|1GCA)/gi) ||
        playerInfo?.cod_funcao === 3003 ||
        playerInfo?.cod_funcao === 4005 ||
        playerInfo?.cod_funcao === 4007 ||
        playerInfo?.cod_funcao === 3002) &&
      totalParticipants >= 31 &&
      totalParticipants <= 50
    ) {
      return true;
    } else if (
      (playerInfo?.cod_funcao === 3003 ||
        playerInfo?.cod_funcao === 4005 ||
        playerInfo?.cod_funcao === 4007 ||
        playerInfo?.cod_funcao === 3002) &&
      totalParticipants >= 51 &&
      totalParticipants <= 100
    ) {
      return true;
    } else if (playerInfo?.cod_funcao === 3002 && totalParticipants > 100) {
      return true;
    } else {
      return false;
    }
  };

  function renderTasksPerStep() {
    if (!tasks) return null;

    try {
      if (tasks?.length) {
        return tasks.map((task: { _id: Key | null | undefined; owner: string | undefined; status: string }) => (
          <div key={task._id}>
            <TaskItem
              data={task}
              editable={false}
              onReload={() => loadData()}
              cycle={activeStepObj}
              playerInfo={playerInfo}
              canApproveParticipants={getCanApproveParticipants(task)}
              canApproveParticipantsInstExpired={activeStepObj?.date_evaluation_step5_finish >= realDatetime}
              isView={true}
            />
          </div>
        ));
      } else {
        return (
          <div className="flex justify-center items-center flex-col space-y-2 lg:space-y-8 bg-maastricht-blue-3 rounded-[20px] p-8 text-center">
            <img src={ImgFeedback} className="max-w-[150px]" alt="Ilustração de caixa vazia" />
            <p tabIndex={0} className="text-lg block text-center font-thin text-white leading-loose tracking-tight">
              <>Nenhuma realização necessita de aprovação</>
            </p>
          </div>
        );
      }
    } catch (e) {
      return null;
    }
  }

  if (!user || !realCycle || !canViewPage) return null;

  if (user?.extra.rede) return <NotFound />;

  return (
    <>
      <div className="mx-auto container p-3 lg:p-10 overflow-hidden">
        <Header
          isRoot={true}
          onSubmitSearch={function (id: any): void {
            console.log(id);
          }}
        />

        {!!isVacationManager && internalUser && (
          <div className="border-2 border-electric-blue rounded-3xl px-8 py-6 mb-12 bg-rich-black bg-opacity-80 ">
            <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-3">
              Você está avaliando por:
            </p>

            <div className="flex flex-row items-center space-x-4">
              <div className="w-[44px] h-[44px]">
                <UserAvatar
                  data={{
                    image: internalUser.image,
                    ...internalUser.extra,
                  }}
                />
              </div>
              <p tabIndex={0}>
                <span className="text-xs font-thin">
                  <strong className="text-base font-semibold">{internalUser.name}</strong> ({internalUser._id})
                </span>
                <span className="block text-xs font-thin">
                  <span className="text-electric-blue uppercase">{internalUser.extra?.dependencia}</span> -{' '}
                  {internalUser.extra?.funcao}
                </span>
              </p>
              <Link
                to="/"
                className={`!ml-8 leading-none ring-2 ring-slate-blue w-full lg:w-auto flex flex-col lg:flex-row items-center bg-slate-blue text-center text-white hover:text-white hover:no-underline no-underline rounded py-2 px-3`}
              >
                SAIR
              </Link>
            </div>
          </div>
        )}

        {!!playerInfo && (
          <div className="border-2 border-electric-blue rounded-3xl px-8 py-6 mb-12 bg-oxford-blue">{clarifyingText}</div>
        )}

        <div className="w-full flex flex-col lg:flex-row lg:justify-between">
          <div className="lg:w-[32.5%]">
            <div className="mb-5">
              <h2 tabIndex={0} className="text-xl text-white block mb-6 font-semibold">
                Histórico de Realizações Destaque
              </h2>

              <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px]">
                {loading ? (
                  <div className="grid grid-cols-1 gap-3">
                    <ArticlePlaceholder />
                  </div>
                ) : (
                  <>
                    <div className="px-8 mb-8">
                      <div>
                        <p tabIndex={0} className="mb-2" id="cycle-label">
                          Escolha o ciclo que deseja acompanhar
                        </p>

                        {optionsCycle && (
                          <div className="mb-4">
                            <CustomSelect
                              placeholder="Clique para selecionar o ciclo"
                              options={optionsCycle}
                              onChange={(e: any) => {
                                setActiveItem(e.value);

                                const selected = realCycle?.find(x => x._id === e.value);

                                if (selected) {
                                  setCycleSelected({
                                    _id: selected._id,
                                    title: selected.title,
                                    version: GetVersion(selected.date_start),
                                  });
                                }
                              }}
                              value={
                                optionsCycle.find(x => x.value === cycleSelected?._id)
                                  ? optionsCycle.find(x => x.value === cycleSelected?._id)
                                  : optionsCycle[getIndexByValue(optionsCycle, activeItem)]
                              }
                            />
                          </div>
                        )}
                      </div>

                      <p tabIndex={0} className="text-white mb-8 text-base font-normal block" id="phase-label">
                        Escolha a fase
                      </p>

                      <ul className="space-y-4 flex flex-col" aria-labelledby="phase-label">
                        {Array.isArray(stages) &&
                          stages.slice(0, numberOfElements).map((step: any) => (
                            <li key={step._id}>
                              <button
                                className={`${
                                  stepActive === step._id ? 'text-alice-blue bg-slate-blue' : 'text-white'
                                } text-sm flex items-center justify-center font-semibold uppercase ring-2 ring-slate-blue rounded w-full h-10`}
                                onClick={() => setStepActive(step._id)}
                              >
                                {step.title}
                              </button>
                            </li>
                          ))}

                        {Array.isArray(stages) && stages.length > 3 && (
                          <button
                            className="mt-4 mx-auto flex items-center text-grey-light justify-center lg:justify-start"
                            onClick={() => (!showMore ? setNumberOfElements(3) : setNumberOfElements(numberOfElements + 6))}
                          >
                            {!showMore ? (
                              <>
                                <BiMinus className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver menos</span>
                              </>
                            ) : (
                              <>
                                <BsPlusLg className="mr-2 inline-block grey-dark" size={17} />
                                <span className="uppercase">Ver mais</span>
                              </>
                            )}
                          </button>
                        )}
                      </ul>
                    </div>

                    {!!resume && (
                      <section className="bg-maastricht-blue rounded-3xl p-8 space-y-4" aria-labelledby="resume-heading">
                        <h3 tabIndex={0} id="resume-heading" className="block text-white leading-none font-normal text-base">
                          Realizações da Fase {activeStepObj?.title}
                        </h3>

                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                          <MdModeStandby className="text-xl" aria-hidden="true" />
                          <span className="flex-1 text-white text-xs leading-none font-thin">
                            Total de realizações aguardando
                          </span>
                          <div
                            className="text-right text-yellow font-normal leading-none text-base"
                            aria-label={`${resume.awaiting} de ${resume.total} realizações aguardando`}
                          >
                            {resume.awaiting}/{resume.total}
                          </div>
                        </div>

                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                          <HiOutlineHandThumbUp className="text-xl" aria-hidden="true" />
                          <span className="flex-1 text-white text-xs leading-none font-thin">
                            Total de realizações autorizadas
                          </span>
                          <div
                            className="text-right text-yellow font-normal leading-none text-base"
                            aria-label={`${resume.approveds} de ${resume.total} realizações autorizadas`}
                          >
                            {resume.approveds}/{resume.total}
                          </div>
                        </div>

                        <div className="space-x-3 flex flex-row items-center justify-between pt-4 border-t border-prussian-blue-2">
                          <HiOutlineHandThumbDown className="text-xl" aria-hidden="true" />
                          <span className="flex-1 text-white text-xs leading-none font-thin">
                            Total de realizações não autorizadas
                          </span>
                          <div
                            className="text-right text-yellow font-normal leading-none text-base"
                            aria-label={`${resume.rejecteds} de ${resume.total} realizações não autorizadas`}
                          >
                            {resume.rejecteds}/{resume.total}
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                )}
              </Box>
            </div>
          </div>

          <div className="lg:w-[64.8%]">
            <h2 tabIndex={0} className="text-xl text-white block mb-6 font-semibold">
              Realizações Destaque da fase {activeStepObj?.status === 'open' ? 'atual' : activeStepObj?.title}
            </h2>

            <Box className="relative bg-prussian-blue border-electric-blue p-4 lg:p-8 border-opacity-100 bg-opacity-100 w-full rounded-[20px] space-y-6">
              {loading ? (
                <div className="grid grid-cols-1 gap-3">
                  <ArticlePlaceholder />
                </div>
              ) : (
                renderTasksPerStep()
              )}
            </Box>
          </div>
        </div>
      </div>

      <Outlet />
    </>
  );
}
